import { useFlag } from '@reflag/react-sdk';

import * as api from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';
import { DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../../navigation';
import { SidebarActionsButton } from '../../../Sidebars/SidebarActionsButton';
import { apiClient, type SiteCapability, useTrialingSiteFeaturePlan } from '../../../state';
import { useSiteAudienceCommand } from '../../AdaptiveContent';
import { getTrialingBadgeProps } from '../../OrganizationBilling';
import { CreateSiteExternalLinkDialog } from './CreateSiteExternalLinkDialog';
import { CreateSiteSectionDialog } from './CreateSiteSectionDialog';
import { CreateSiteSectionGroupDialog } from './CreateSiteSectionGroupDialog';
import { getRestrictedSiteItemPaletteGroups } from './getRestrictedSiteItemPaletteGroups';
import { type UseAddSectionsToGroupProps, useAddSectionsToGroup } from './useAddSectionsToGroup';
import { useDeleteSiteItems } from './useDeleteSiteItems';
import {
    getSiteSectionGroupMoveDestinations,
    useMoveSiteSectionGroup,
} from './useMoveSiteSectionGroup';
import { useSiteContentDraftCommand } from './useSiteContentDraft';

export interface SiteSectionGroupActionsButtonProps
    extends
        Pick<ButtonProps, 'size' | 'disabled'>,
        Omit<
            UseAddSectionsToGroupProps,
            'createSiteSectionDialogTrigger' | 'includeExistingSections'
        > {
    /**
     * Controls whether the trigger should match sidebar item actions.
     */
    kind?: 'default' | 'sidebar';
    siteSectionGroup: api.SiteSectionGroup;
    sitePermissions: SiteCapability;
    site: api.Site;
    organization: api.Organization;
    siteStructure: api.SiteStructure;
    onCreateSection?: (siteSection: api.SiteSection) => void;
    onCreateSectionGroup?: (siteSectionGroup: api.SiteSectionGroup) => void;
    onCreateExternalLink?: (siteExternalLink: api.SiteExternalLink) => void;
}

/**
 * Action button to open actions palette for a section group.
 */
export function SiteSectionGroupActionsButton(props: SiteSectionGroupActionsButtonProps) {
    const {
        siteSectionGroup,
        sitePermissions,
        organization,
        site,
        siteStructure,
        upgrade,
        onCreateSection,
        onCreateSectionGroup,
        onCreateExternalLink,
        kind = 'default',
        ...buttonProps
    } = props;

    const deleteSiteItems = useDeleteSiteItems({ organization, site });
    const createSiteSectionGroupDialogTrigger = useDialogTriggerState();
    const createSiteSectionDialogTrigger = useDialogTriggerState();
    const createSiteExternalLinkTrigger = useDialogTriggerState();
    const trialingPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesSections);
    const addSectionsToGroup = useAddSectionsToGroup({
        ...props,
        createSiteSectionDialogTrigger,
        includeExistingSections: sitePermissions.canCreateSite,
    });
    const { navigate } = useNavigation();
    const siteAudience = useSiteAudienceCommand({
        organizationId: organization.id,
        site,
        target: 'section-group',
        condition: siteSectionGroup.condition ? siteSectionGroup.condition : undefined,
        onChange: async (condition) => {
            await apiClient.siteSectionGroups.updateSiteSectionGroupById(
                organization.id,
                site.id,
                siteSectionGroup.id,
                {
                    condition: condition ?? null,
                },
                { returnPreference: 'minimal' }
            );
        },
    });

    const draftCommand = useSiteContentDraftCommand({
        organization,
        site,
        siteStructure,
        content: siteSectionGroup,
        canCreateSite: sitePermissions.canCreateSite,
    });

    const featureNestedSectionGroup = useFlag('NESTED_SECTION_GROUPS');
    const moveSiteSectionGroup = useMoveSiteSectionGroup({
        organization,
        site,
        siteStructure,
        nestedSectionGroupsEnabled: featureNestedSectionGroup?.isEnabled ?? false,
    });
    const canMoveSiteSectionGroup =
        sitePermissions.canCreateSite &&
        getSiteSectionGroupMoveDestinations({
            siteSectionGroup,
            siteStructure,
            nestedSectionGroupsEnabled: featureNestedSectionGroup?.isEnabled ?? false,
        }).length > 0;
    const canAuthorExternalLinks =
        sitePermissions.canAdminSite && siteStructure.type === 'sections';

    if (!sitePermissions.canEditSite) {
        return null;
    }

    const palette = () => {
        if (upgrade) {
            return createPalette({
                groups: getRestrictedSiteItemPaletteGroups({
                    item: siteSectionGroup,
                    upgrade,
                    deleteSiteItems,
                    sitePermissions,
                }),
            });
        }
        return createPalette({
            groups: [
                kind === 'sidebar' && sitePermissions.canCreateSite
                    ? {
                          key: 'edit',
                          commands: [
                              {
                                  key: 'siteSectionGroup.edit',
                                  title: 'Edit navigation item',
                                  icon: Icon.Edit,
                                  trigger: () => {
                                      navigate({
                                          screen: {
                                              type: 'site',
                                              organization: organization.id,
                                              site: site.id,
                                              tab: {
                                                  type: 'structure',
                                                  selected: siteSectionGroup.id,
                                              },
                                          },
                                      });
                                  },
                              },
                          ],
                      }
                    : null,
                {
                    key: 'add-sections',
                    commands: [
                        !siteSectionGroup.sectionGroup && featureNestedSectionGroup?.isEnabled
                            ? {
                                  key: 'add-new-group',
                                  title: 'New group',
                                  icon: Icon.AddSiteGroup,
                                  badge: trialingPlan
                                      ? getTrialingBadgeProps({
                                            plan: trialingPlan,
                                            size: 'small',
                                            thin: true,
                                        })
                                      : undefined,
                                  trigger: createSiteSectionGroupDialogTrigger.open,
                              }
                            : null,
                        {
                            key: 'add-sections',
                            title: sitePermissions.canCreateSite ? 'Add sections…' : 'Add section…',
                            icon: Icon.AddSiteSection,
                            descendant: () => addSectionsToGroup(siteSectionGroup),
                        },
                        canAuthorExternalLinks
                            ? {
                                  key: 'add-external-link',
                                  title: 'New external link',
                                  icon: Icon.Link,
                                  trigger: createSiteExternalLinkTrigger.open,
                              }
                            : null,
                    ].filter(filterOutNullable),
                },
                sitePermissions.canCreateSite
                    ? {
                          key: 'options',
                          commands: [
                              draftCommand,
                              siteAudience?.command ?? null,
                              canMoveSiteSectionGroup
                                  ? {
                                        key: 'move-group',
                                        title: 'Move to…',
                                        icon: Icon.ArrowRight,
                                        descendant: () => moveSiteSectionGroup(siteSectionGroup),
                                    }
                                  : null,
                              {
                                  key: 'remove-group',
                                  title: 'Remove',
                                  icon: Icon.Trashcan,
                                  trigger: () => deleteSiteItems([siteSectionGroup]),
                              },
                          ].filter(filterOutNullable),
                      }
                    : {
                          key: 'draft-options',
                          commands: [draftCommand],
                      },
            ].filter(filterOutNullable),
        });
    };

    return (
        <>
            {siteAudience?.dialogs}
            <DialogRoot {...createSiteSectionGroupDialogTrigger.rootProps}>
                <CreateSiteSectionGroupDialog
                    site={site}
                    organization={organization}
                    siteSections={[]}
                    parent={siteSectionGroup.id}
                    onCreateSectionGroup={onCreateSectionGroup}
                />
            </DialogRoot>
            <DialogRoot {...createSiteSectionDialogTrigger.rootProps}>
                <CreateSiteSectionDialog
                    organization={organization}
                    site={site}
                    siteStructure={siteStructure}
                    inSiteSectionGroup={siteSectionGroup}
                    onCreateSection={onCreateSection}
                />
            </DialogRoot>
            <DialogRoot {...createSiteExternalLinkTrigger.rootProps}>
                <CreateSiteExternalLinkDialog
                    organization={organization}
                    site={site}
                    siteStructure={siteStructure}
                    parent={siteSectionGroup}
                    onCreateExternalLink={onCreateExternalLink}
                />
            </DialogRoot>
            {kind === 'sidebar' ? (
                <SidebarActionsButton palette={palette} disabled={buttonProps.disabled} />
            ) : (
                <PaletteButton
                    palette={palette}
                    kind="blank"
                    label="Actions"
                    leadingIcon={Icon.MoreVertical}
                    iconOnly
                    {...buttonProps}
                />
            )}
        </>
    );
}
